import React, { useContext } from 'react';
import { t } from '@sm/intl';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { StaticContext } from '@sm/webassets';
import useFeatureSetStyles from './useStyles';
import featureList, { packageFeaturesIncluded, combinedFeatureValues } from './features';
import { formatSummaryFeature } from '~app/pages/Pricing/lib/PackageFeatures';
import { DisplayFeatureStatic } from '~shared/types/pricing/features';
import EnabledCheckMark from '~app/pages/Pricing/components/PricingFooter/components/EnabledCheckMark';
import DisabledCheckMark from '~app/pages/Pricing/components/PricingFooter/components/DisabledCheckMark';
import { Packages, PackageDisplayNames } from '~shared/constants/pricing';
import COPY from '~pricing/experiments/grow_2792_pnp_refresh_v1/copy/ippmCopy';

import { PackageDisplayNames as Grow2792PackageDisplayNames } from '~pricing/experiments/grow_2792_pnp_refresh_v1/constants';

export default function FeatureSet({
  packageName,
  hasPaidFeatures = false,
}: {
  packageName: Packages;
  hasPaidFeatures: boolean;
}): React.ReactElement {
  const { Features, FeatureList, FeatureItem } = useFeatureSetStyles();
  const {
    environment: { slLanguageLocale },
  } = useContext(StaticContext);

  const expPackageDisplayNames = { ...PackageDisplayNames, ...Grow2792PackageDisplayNames };

  const filteredFeatures = featureList.map(feature => {
    let featureItem;
    const packageFeature = combinedFeatureValues[packageName as keyof typeof combinedFeatureValues];

    if (feature === 'response_count') {
      const { name, amount, frequency } = packageFeature[feature];

      featureItem = formatSummaryFeature(name, { amount: amount.toLocaleString(slLanguageLocale), frequency });
    }

    if (['support', 'exports', 'colors'].includes(feature)) {
      featureItem = formatSummaryFeature(packageFeature[feature as keyof typeof packageFeature]?.name);
    }

    if (featureItem) {
      featureItem.name = feature;
    } else {
      featureItem = formatSummaryFeature(feature);
    }

    return featureItem;
  });

  return (
    <Box className={Features}>
      <Typography color="dark" variant="bodySm" weight="medium">
        {t(hasPaidFeatures ? COPY.ALSO_INCLUDED_IN : COPY.INCLUDED_IN, {
          package: t(expPackageDisplayNames[packageName]),
        })}
      </Typography>
      <ul className={FeatureList}>
        {filteredFeatures.map((feature: DisplayFeatureStatic) => {
          return (
            <li className={FeatureItem} key={`${packageName}-feature-${feature?.summary}`}>
              {feature?.name &&
              packageFeaturesIncluded[feature?.name as keyof typeof packageFeaturesIncluded].includes(packageName) ? (
                <EnabledCheckMark />
              ) : (
                <DisabledCheckMark />
              )}
              <Typography color="dark" variant="bodySm">
                {feature?.summary}
              </Typography>
            </li>
          );
        })}
      </ul>
    </Box>
  );
}
